import { Col, Row } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import numberHumanizer from '@/helpers/numberHumanizer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { calculatePayableAmount } from '../../utils';
import type { OrderSummaryProps, OrderSummaryRowProps } from '../../types/purchaseTypes';

function OrderSummaryRow({
  label,
  value,
  isLoading,
  badge,
  description,
  isDanger,
  unit = 'تومان',
  showValue = true,
}: OrderSummaryRowProps) {
  if (!showValue || (value === undefined && value !== 0)) return null;

  return (
    <>
      <hr />
      <Col md={12} className="pb-3">
        {label}
        {badge && (
          <SubtleBadge pill bg="success" className="ms-2">
            <span>{badge}</span>
          </SubtleBadge>
        )}
        {description && (
          <div className="fs--2 mt-1 d-flex align-items-center gap-1">
            <FontAwesomeIcon icon="exclamation-circle" className="text-warning" />
            {description}
          </div>
        )}
        <span className="float-end">
          {isLoading ? (
            <TextLoading height={17} isWidthPx width={120} />
          ) : (
            <>
              <h5 className={`d-inline ${isDanger ? 'text-danger' : ''}`}>
                {typeof value === 'number' && unit === 'تومان' 
                  ? numberHumanizer(value) 
                  : value}
              </h5>
              {unit && (
                <h6 className={`d-inline ${isDanger ? 'text-danger' : ''} ms-1`}>
                  {unit}
                </h6>
              )}
            </>
          )}
        </span>
      </Col>
    </>
  );
}

function OrderSummary({ selectedTariff, introCodeData, isLoadingTariffData }: OrderSummaryProps) {
  const payableAmount = calculatePayableAmount(selectedTariff, introCodeData);

  return (
    <div className="container mt-5 border border-dashed rounded-4 border-2 w-lg-50">
      <Row className="p-3">
        <Col md={12} className="pb-3">
          <span>طرح انتخابی شما</span>
          <h5 className="d-inline float-end">
            {isLoadingTariffData ? (
              <TextLoading height={16} isWidthPx width={160} />
            ) : (
              <>
                {selectedTariff?.data?.tariff}
                <span className="fs-0 mx-1 d-inline">
                  ({selectedTariff?.data?.period})
                </span>
              </>
            )}
          </h5>
        </Col>

        <OrderSummaryRow
          label="مبلغ تعرفه انتخابی"
          value={selectedTariff?.data?.price}
          isLoading={isLoadingTariffData}
        />

        <OrderSummaryRow
          label="میزان تخفیف"
          value={selectedTariff?.data?.discount_amount}
          badge={selectedTariff?.data?.discount ? `${selectedTariff.data.discount}%` : null}
          isLoading={isLoadingTariffData}
          isDanger={true}
        />

        <OrderSummaryRow
          label="مبلغ مانده از طرح قبلی"
          value={selectedTariff?.data?.remaining_amount_from_previous_tariff}
          isLoading={isLoadingTariffData}
        />

        <OrderSummaryRow
          label="تعداد روزهای مانده از طرح قبلی"
          value={selectedTariff?.data?.remaining_days_from_previous_tariff}
          isLoading={isLoadingTariffData}
          unit="روز"
          showValue={selectedTariff?.data?.remaining_days_from_previous_tariff !== 0}
        />

        <OrderSummaryRow
          label="مالیات بر ارزش افزوده (%۱۰)"
          value={selectedTariff?.data?.tax_amount}
          isLoading={isLoadingTariffData}
        />

        {introCodeData?.data?.discount_amount ? (
          <OrderSummaryRow
            label={
              introCodeData.data.code_type === 'affiliate'
                ? 'تخفیف کد معرف'
                : 'تخفیف کد'
            }
            value={introCodeData.data.discount_amount}
            isLoading={isLoadingTariffData}
            isDanger={true}
          />
        ) : null}

        <hr className="border-3" />
        <Col>
          <h5 className="d-inline">مبلغ قابل پرداخت</h5>
          <h4 className="text-success d-inline float-end">
            {isLoadingTariffData ? (
              <TextLoading height={20} isWidthPx width={150} />
            ) : (
              <>
                {numberHumanizer(payableAmount)} تومان
              </>
            )}
          </h4>
        </Col>
      </Row>
    </div>
  );
};

export default OrderSummary;

